import { ActionPermissionSetFragment } from "@src/features/roles/dialogs/NewRoleDialog.generated";
import {
  UserActionPermissionValueFragment,
  UserActionPermissionsFragment,
} from "@src/lib/contexts/ActionPermissionsContext.generated";
import {
  ActionPermissionSetInput,
  Ff,
  OptionalBooleanValue,
  UserType,
} from "@src/types.generated";
import { FeatureFlagContext } from "@src/lib/contexts/FeatureFlagContext";
import { resolveFeatureFlag } from "@src/features/app/hooks/useFeatureFlag";
import { useContext } from "react";

export type UserActionPermissionsKey = keyof Omit<
  UserActionPermissionsFragment,
  "__typename"
>;

export const isUserActionPermissionValue = (
  value: UserActionPermissionValueFragment | string,
): value is UserActionPermissionValueFragment => {
  return (
    (value as UserActionPermissionValueFragment).__typename ===
    "UserActionPermissionValue"
  );
};

export type PermissionDetails = {
  name: string;
  description?: string;
  showWhenFeatureFlagsEnabled?: Ff[];
  hideWhenFeatureFlagsEnabled?: Ff[];
};

export const USER_ACTION_PERMISSIONS_NAME_AND_DESCRIPTION: Record<
  UserActionPermissionsKey,
  PermissionDetails
> = {
  changeOrgSettings: {
    name: "Change Org Settings",
    description: "Manage roles, locations, brand, change user type, etc",
  },
  editTrainees: {
    name: "Edit Trainees",
    description: "Edit someone's name, roles, locations, etc",
  },
  deactivateTrainees: {
    name: "Deactivate Trainees",
    description: "Deactivate or reactivate a user",
  },
  manageOrgIntegrations: {
    name: "Manage Org Integrations",
    description: "Manage integrations like HRIS",
  },
  manageBilling: {
    name: "Manage Billing",
    description: "Configure payment method / view billing history",
  },
  inviteOthers: {
    name: "Invite Others",
    description: "Invite new users to the org",
  },
  createContent: {
    name: "Create Content",
    description: "Create courses, modules, files, or check-ins",
  },
  manageLibrary: {
    name: "Manage Library",
    description: "Manage tags, archive/unarchive content",
  },
  assignContent: {
    name: "Assign Content",
    description: "Manually assign any non-premium content",
  },
  unassignContent: {
    name: "Unassign Content",
    description: "Manually unassign any non-premium content",
  },
  updateAutomations: {
    name: "Update Automations and Library Access",
    description: "Manage content automations",
  },
  verifySkills: {
    name: "Complete all check-ins",
    description: "Can complete any check-ins at their locations",
  },
  viewReporting: {
    name: "View Reporting",
    description: "View and export reporting data",
  },
  sendMessages: {
    name: "Send Messages",
    description: "Send messages in the dashboard and mobile app",
    hideWhenFeatureFlagsEnabled: [Ff.NewMessaging],
  },
  sendPosts: {
    name: "Send Posts",
    description: "Send Newsfeed posts.",
    showWhenFeatureFlagsEnabled: [Ff.NewMessaging],
  },
  managePremiumContent: {
    name: "Manage Premium Content",
    description: "Manage content like Sexual Harassment Prevention Training",
  },
  promoteTrainees: {
    name: "Promote Trainees",
    description: "Promote Trainees to Manager  in Opus",
  },
  markAsComplete: {
    name: "Mark as Complete",
    description: "Mark any content as complete",
  },
  viewAllSubmittedTaskLists: {
    name: "View all Checklist submissions",
    description: "View all Checklist submissions across all locations",
  },
  createChat: {
    name: "Create Chats",
    description:
      "Create 1:1 and Group Chats. Trainees and Managers can only create chats with people at their locations.",
    showWhenFeatureFlagsEnabled: [Ff.NewMessaging],
  },
};

export const useUserActionPermissionDetails = (): Record<
  UserActionPermissionsKey,
  PermissionDetails
> => USER_ACTION_PERMISSIONS_NAME_AND_DESCRIPTION;

const PERMISSION_KEYS_WITH_NAME = Object.keys(
  USER_ACTION_PERMISSIONS_NAME_AND_DESCRIPTION,
) as UserActionPermissionsKey[];

export const useIsUserActionPermissionVisible = (): ((
  key: UserActionPermissionsKey,
) => boolean) => {
  const flags = useContext(FeatureFlagContext);
  return (key: UserActionPermissionsKey) => {
    const { showWhenFeatureFlagsEnabled, hideWhenFeatureFlagsEnabled } =
      USER_ACTION_PERMISSIONS_NAME_AND_DESCRIPTION[key];
    if (
      hideWhenFeatureFlagsEnabled?.length &&
      hideWhenFeatureFlagsEnabled.every((flag) =>
        resolveFeatureFlag(flags, flag),
      )
    ) {
      return false;
    }
    return (showWhenFeatureFlagsEnabled || []).every((flag) =>
      resolveFeatureFlag(flags, flag),
    );
  };
};

type RequiredAndOptionalRolePermissions = {
  required: UserActionPermissionsKey[];
  optionalDefaultChecked: UserActionPermissionsKey[];
  optionalDefaultUnchecked?: UserActionPermissionsKey[];
};

export const USER_TYPE_TO_PERMISSIONS: Record<
  UserType,
  RequiredAndOptionalRolePermissions
> = {
  [UserType.Admin]: {
    required: ["editTrainees", "deactivateTrainees"],
    optionalDefaultChecked: [
      "changeOrgSettings",
      "manageOrgIntegrations",
      "manageBilling",
      "inviteOthers",
      "createContent",
      "manageLibrary",
      "assignContent",
      "unassignContent",
      "updateAutomations",
      "verifySkills",
      "viewReporting",
      "sendMessages",
      "sendPosts",
      "managePremiumContent",
      "promoteTrainees",
      "markAsComplete",
      "createChat",
    ],
  },
  [UserType.Manager]: {
    required: ["inviteOthers"],
    optionalDefaultChecked: [
      "createContent",
      "manageLibrary",
      "assignContent",
      "unassignContent",
      "verifySkills",
      "viewReporting",
      "sendMessages",
      "editTrainees",
      "deactivateTrainees",
      "promoteTrainees",
    ],
    optionalDefaultUnchecked: [
      "markAsComplete",
      "viewAllSubmittedTaskLists",
      "createChat",
      "sendPosts",
    ],
  },
  [UserType.Employee]: {
    required: [],
    optionalDefaultChecked: ["verifySkills"],
    optionalDefaultUnchecked: ["createChat", "sendPosts"],
  },
};

export const createActionPermissionSetInputWithDefault = (
  defaultValue: boolean | undefined,
): ActionPermissionSetInput => {
  const input = PERMISSION_KEYS_WITH_NAME.reduce(
    (
      acc: Partial<{ [key in UserActionPermissionsKey]: OptionalBooleanValue }>,
      key: UserActionPermissionsKey,
    ) => {
      acc[key] = {
        value: defaultValue,
      };
      return acc;
    },
    {},
  ) as { [key in UserActionPermissionsKey]: OptionalBooleanValue };
  return input;
};

export const createDefaultActionPermissionSetInputForUserType = (
  userType?: UserType,
): ActionPermissionSetInput => {
  const input = createActionPermissionSetInputWithDefault(false);
  if (!userType) {
    return input;
  }
  const { required, optionalDefaultChecked, optionalDefaultUnchecked } =
    USER_TYPE_TO_PERMISSIONS[userType];
  const checkedKeys = [...required, ...optionalDefaultChecked];
  const uncheckedKeys = optionalDefaultUnchecked || [];
  const newInput = { ...input };
  if (userType === UserType.Employee) {
    return newInput;
  }
  checkedKeys.forEach((key) => {
    newInput[key] = {
      value: true,
    };
  });
  uncheckedKeys.forEach((key) => {
    newInput[key] = {
      value: false,
    };
  });
  return newInput;
};

export const actionPermissionsFragmentToInput = (
  actionPermissionSet?: ActionPermissionSetFragment,
): ActionPermissionSetInput => {
  // Base on "inherit" rather than all-false so a permission the fragment
  // doesn't select (e.g. one added after this fragment was last updated)
  // defaults to inherit instead of being silently denied.
  const input = { ...createActionPermissionSetInputWithDefault(undefined) };
  if (!actionPermissionSet) {
    return input;
  }
  Object.entries(actionPermissionSet).forEach(([key, value]) => {
    if (key in USER_ACTION_PERMISSIONS_NAME_AND_DESCRIPTION) {
      input[key as UserActionPermissionsKey] = {
        value: typeof value === "boolean" ? value : undefined,
      };
    }
  });
  return input as ActionPermissionSetInput;
};
